/**
 * Phase 5Z.19b — Firm Switcher Context & Top-Bar UI
 *
 * Holds the currently active firm_id in React state + localStorage and
 * registers an axios interceptor that adds the `X-Active-Firm` header to
 * every API call so the backend can scope list endpoints.
 *
 * For admin/GF: any firm (or "all").
 * For restricted users: only their assigned firm_ids; if exactly one,
 * lock the switcher and show it as read-only.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, ChevronDown, Check } from "lucide-react";

const STORAGE_KEY = "munja.active_firm_id";
const FirmContext = createContext({
  firms: [], activeFirmId: null, setActiveFirmId: () => {}, canSwitchAll: false,
});

export function FirmProvider({ children }) {
  const { user } = useAuth();
  const [firms, setFirms] = useState([]);
  const [activeFirmId, _setActiveFirmId] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [meta, setMeta] = useState({ can_switch_all: false, firm_ids: [] });

  // Load firms list whenever the user changes
  useEffect(() => {
    if (!user) { setFirms([]); return; }
    let cancelled = false;
    api.get("/firms").then(({ data }) => {
      if (!cancelled) setFirms(data || []);
    }).catch(() => setFirms([]));
    api.get("/firms/me/active").then(({ data }) => {
      if (!cancelled) setMeta(data || {});
    }).catch(() => setMeta({}));
    return () => { cancelled = true; };
  }, [user]);

  // Auto-pin single-firm users to their one firm (no choice)
  useEffect(() => {
    if (!meta) return;
    if (!meta.can_switch_all && Array.isArray(meta.firm_ids) && meta.firm_ids.length === 1) {
      const onlyId = meta.firm_ids[0];
      if (activeFirmId !== onlyId) setActiveFirmId(onlyId);
    }
  }, [meta]); // eslint-disable-line

  function setActiveFirmId(id) {
    _setActiveFirmId(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }

  // Register the X-Active-Firm axios interceptor exactly once
  useEffect(() => {
    const id = api.interceptors.request.use((cfg) => {
      const stored = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
      if (stored) cfg.headers["X-Active-Firm"] = stored;
      else delete cfg.headers["X-Active-Firm"];
      return cfg;
    });
    return () => api.interceptors.request.eject(id);
  }, []);

  const value = useMemo(() => ({
    firms,
    activeFirmId,
    setActiveFirmId,
    canSwitchAll: !!meta?.can_switch_all,
    allowedFirmIds: meta?.firm_ids || [],
  }), [firms, activeFirmId, meta]);

  return <FirmContext.Provider value={value}>{children}</FirmContext.Provider>;
}

export function useFirms() { return useContext(FirmContext); }

// ---------------------------------------------------------------------------
// Top-bar Firm Switcher
// ---------------------------------------------------------------------------
export default function FirmSwitcher() {
  const { firms, activeFirmId, setActiveFirmId, canSwitchAll, allowedFirmIds } = useFirms();
  const [open, setOpen] = useState(false);

  // If user has only one firm and cannot switch, hide the picker entirely
  if (!canSwitchAll && allowedFirmIds.length <= 1) {
    if (firms.length === 0) return null;
    const f = firms[0] || {};
    return (
      <div
        className="hidden md:flex items-center gap-2 px-2.5 py-1.5 rounded bg-zinc-900/60 border border-zinc-800"
        data-testid="firm-switcher-locked"
        title={`Firma: ${f.name}`}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: f.color || "#3b82f6" }} />
        <span className="text-xs text-zinc-300 truncate max-w-[160px]">{f.name}</span>
      </div>
    );
  }

  // If there's only one firm in the system → nothing to switch
  if (firms.length <= 1) return null;

  const active = firms.find((f) => f.id === activeFirmId);
  return (
    <div className="relative" data-testid="firm-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="firm-switcher-trigger"
        className="hidden md:inline-flex items-center gap-2 px-2.5 py-1.5 rounded bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition-colors"
      >
        {active ? (
          <>
            <span className="w-2 h-2 rounded-full" style={{ background: active.color || "#3b82f6" }} />
            <span className="text-xs font-medium text-zinc-100 truncate max-w-[160px]">{active.name}</span>
          </>
        ) : (
          <>
            <Building2 className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-xs text-zinc-300">Alle Firmen</span>
          </>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />
      </button>

      {open && (
        <>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
            tabIndex={-1}
            aria-hidden
          />
          <div
            className="absolute right-0 top-full mt-1 z-50 w-64 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl py-1.5"
            data-testid="firm-switcher-menu"
          >
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              Aktive Firma
            </div>
            {canSwitchAll && (
              <button
                onClick={() => { setActiveFirmId(null); setOpen(false); }}
                data-testid="firm-switcher-all"
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-800 ${
                  activeFirmId === null ? "text-zinc-100" : "text-zinc-300"
                }`}
              >
                <Building2 className="w-4 h-4 text-zinc-500" />
                <span className="flex-1 text-left">Alle Firmen</span>
                {activeFirmId === null && <Check className="w-3.5 h-3.5 text-emerald-400" />}
              </button>
            )}
            <div className="border-t border-zinc-800 my-1" />
            {firms.map((f) => (
              <button
                key={f.id}
                onClick={() => { setActiveFirmId(f.id); setOpen(false); }}
                data-testid={`firm-switcher-item-${f.id}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-800 ${
                  activeFirmId === f.id ? "text-zinc-100" : "text-zinc-300"
                }`}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: f.color || "#3b82f6" }} />
                <span className="flex-1 text-left truncate">{f.name}</span>
                {f.is_default && <span className="text-[9px] uppercase text-amber-400 font-semibold">std</span>}
                {activeFirmId === f.id && <Check className="w-3.5 h-3.5 text-emerald-400" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
